import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ApiClientService, PermissionService, ScrollService, ToastService } from '@services/index';
import { TablePreferencesService } from '@services/local-storage/table-preferences.service';
import { Align, ExportType, Permission } from 'app/enums';
import {
    BehaviorSubject,
    catchError,
    concatMap,
    debounceTime,
    EMPTY,
    finalize,
    map,
    merge,
    Observable,
    of,
    Subject,
    switchMap,
    take,
    takeWhile,
    tap,
    timer
} from 'rxjs';
import { Filter, FilterForm } from './measurement-list.model';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { APP_ROUTES } from 'app/constants';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalHelper } from '@helpers/modal-helper';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
    FilterComponent,
    TableType,
    deserializeDateRange
} from 'app/features/shared/component/filter/filter.component';
import {
    BulkExportJobResponse,
    GetMeasurementResponse,
    PaginatedResponse,
    SavedFilterResponse
} from '@services/api-client/responses';
import { FilterRequest } from '@services/api-client/requests';
import { FilterConfig, SelectedFilter } from 'app/features/shared/component/filter/filter-model';
import { PaginationChange } from '@shared/components/pagination/pagination.component';
import { MeasurementRoutes } from '../../measurement.routes';
import {
    MarkerAnnotationModalComponent,
    MarkerAnnotationModalData,
    MoveLocationModalComponent,
    MoveLocationModalData,
    SaveFilterModalComponent
} from '../../components';
import { MeasurementListFilterService } from '../../services/measurement-list-filter.service';

@UntilDestroy()
@Component({
    selector: 'app-measurement-list',
    templateUrl: './measurement-list.component.html',
    styleUrl: './measurement-list.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class MeasurementListComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild(FilterComponent) public readonly filterComponent: FilterComponent;
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly apiClientService = inject(ApiClientService);
    private readonly translateService = inject(TranslateService);
    private readonly measurementListFilterService = inject(MeasurementListFilterService);
    private readonly router = inject(Router);
    private readonly modalService = inject(NgbModal);
    private readonly toastService = inject(ToastService);
    private readonly permissionService = inject(PermissionService);
    private readonly tablePreferencesService = inject(TablePreferencesService);
    private readonly scrollService = inject(ScrollService);

    private static readonly tableId = 'measurement-list' as const;
    private static readonly sortKeyMapping: Record<string, string> = {
        location: 'location.name',
        device: 'device.name',
        user: 'user.name'
        // Add more mappings as needed for nested properties
    };

    public readonly align = Align;
    public sortKey: string = 'created';
    public sortAscending = false;
    public loading = false;
    public filterForm: FormGroup<FilterForm>;
    private readonly defaultFilter: Filter = {
        searchPhrase: ''
    };
    public showFilter = false;
    public showHeader = false;
    public isFilterEmpty = true;
    public readonly tableType = TableType.Measurements;
    public filterConfig: FilterConfig = {
        fields: [
            'users',
            'devices',
            'locations',
            'captureDateRange',
            'uploadDateRange',
            'firstAmplitude',
            'lastAmplitude',
            'firstResult',
            'lastResult',
            'ptcUse'
        ]
    };

    public includeFlags: Record<string, boolean> = {};
    public loadingMeasurementIds: string[] = [];
    public isMoveLoading = false;
    public isDeleteLoading = false;
    private latestMeasurements: GetMeasurementResponse[] = [];
    public permissions = Permission;
    public userPermissions: Permission[] = this.permissionService.userPermissions;
    public isUnrestricted = this.permissionService.isUnrestricted();

    public downloading = {
        excel: false,
        csv: false,
        rawData: false
    };
    public bulkExportProgress: { processedFiles: number; totalFiles: number } | null = null;
    public exportType = ExportType;

    public savedFilters: SavedFilterResponse[] = [];
    public savingFilter = false;
    public showScorecardComparison = false;

    private readonly deletedMeasurementIdsSubject: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
    public deletedMeasurementIds$: Observable<string[]> = this.deletedMeasurementIdsSubject.asObservable();
    public measurementsLength = 0;
    public currentPage = 1;
    public pageSize = 200;

    public get f() {
        return this.filterForm.controls;
    }

    private getSortKeyForBackend(sortKey: string): string {
        const keyStr = sortKey.toString().toLowerCase();
        return MeasurementListComponent.sortKeyMapping[keyStr] || sortKey.toString();
    }

    private readonly searchSubject = new Subject<void>();
    private readonly getMeasurementSubject = new BehaviorSubject<SelectedFilter>(null);
    public measurements$ = merge(this.searchSubject.pipe(debounceTime(700)), this.getMeasurementSubject).pipe(
        switchMap(() => {
            // Guard against filterComponent being undefined during initialization
            if (!this.filterComponent) {
                return of([]);
            }

            this.loading = true;
            const predicates = this.filterComponent.getPredicates();
            const searchValue = this.f.searchPhrase.value?.trim();
            const filterRequest: FilterRequest = {
                page: this.currentPage,
                pageSize: this.pageSize,
                predicates,
                search: searchValue || undefined,
                sortKey: this.getSortKeyForBackend(this.sortKey),
                sortAscending: this.sortAscending
            };

            return merge(
                of(null),
                this.apiClientService.getMeasurements$(filterRequest).pipe(
                    catchError(() => {
                        const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                        const message = this.translateService.instant(
                            'FEATURES.MEASUREMENTS.TOAST_MESSAGES.GET_MEASUREMENTS_ERROR'
                        ) as string;
                        this.toastService.open(title, message, { type: 'error', progressBar: true });
                        this.loading = false;
                        this.changeDetectorRef.markForCheck();
                        return of({
                            items: [],
                            total: 0,
                            page: 1,
                            pageSize: 25,
                            totalPages: 0,
                            hasNextPage: false,
                            hasPreviousPage: false
                        });
                    }),
                    map((response: PaginatedResponse<GetMeasurementResponse>) => {
                        this.measurementsLength = response.total;
                        return response.items;
                    }),
                    switchMap((measurement: GetMeasurementResponse[]) =>
                        this.deletedMeasurementIds$.pipe(
                            map((deletedMeasurementIds) =>
                                measurement.filter((c) => !deletedMeasurementIds.includes(c.id))
                            )
                        )
                    ),
                    map((measurements: GetMeasurementResponse[]) => {
                        this.latestMeasurements = measurements;
                        const visibleIds = new Set(measurements.map((m) => m.id));
                        this.includeFlags = Object.fromEntries(
                            Object.entries(this.includeFlags).filter(([id]) => visibleIds.has(id))
                        );

                        const savedPosition = this.tablePreferencesService.getPreferences(
                            MeasurementListComponent.tableId
                        )?.scrollPosition;
                        if (savedPosition) {
                            setTimeout(() => this.scrollService.scrollTo(savedPosition));
                        }

                        this.loading = false;
                        this.changeDetectorRef.markForCheck();
                        return measurements;
                    })
                )
            );
        })
    );

    public ngOnInit() {
        const saved = this.tablePreferencesService.getPreferences(MeasurementListComponent.tableId);
        if (saved?.sort) {
            this.sortKey = saved.sort.key;
            this.sortAscending = saved.sort.ascending;
        } else {
            // Save default sort to preferences so other components (like select-measurement) get the same defaults
            this.tablePreferencesService.setPreferences(MeasurementListComponent.tableId, {
                sort: { key: this.sortKey, ascending: this.sortAscending }
            });
        }

        // Restore pagination preferences
        if (saved?.currentPage !== undefined) {
            this.currentPage = saved.currentPage;
        } else {
            // Save default currentPage to preferences
            this.tablePreferencesService.setPreferences(MeasurementListComponent.tableId, {
                currentPage: this.currentPage
            });
        }
        if (saved?.pageSize !== undefined) {
            this.pageSize = saved.pageSize;
        } else {
            // Save default pageSize to preferences so other components (like select-measurement) get the same default
            this.tablePreferencesService.setPreferences(MeasurementListComponent.tableId, {
                pageSize: this.pageSize
            });
        }

        // Check if returning from a measurement detail page - only then restore scroll position
        const previousUrl = this.router.lastSuccessfulNavigation?.previousNavigation?.finalUrl?.toString() ?? '';
        const isReturningFromDetail = previousUrl.includes('/measurement/details/');

        // Only restore scroll position when returning from detail page, otherwise start at top
        if (!isReturningFromDetail) {
            this.tablePreferencesService.setPreferences(MeasurementListComponent.tableId, {
                scrollPosition: 0
            });
        }

        const fb = new FormBuilder();
        if (this.isUnrestricted) {
            this.filterConfig.fields.push('tenants');
        }

        this.filterForm = fb.nonNullable.group(this.defaultFilter);

        // Restore search phrase if we have saved data
        if (saved?.searchPhrase) {
            this.f.searchPhrase.setValue(saved.searchPhrase, { emitEvent: false });
        }

        this.f.searchPhrase.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
            this.currentPage = 1; // Reset pagination on search
            this.tablePreferencesService.setPreferences(MeasurementListComponent.tableId, {
                searchPhrase: this.f.searchPhrase.value || '',
                currentPage: 1
            });
            this.searchSubject.next();
        });
        this.loadSavedFilters();
    }

    public ngAfterViewInit(): void {
        // Trigger initial fetch now that filterComponent is available
        this.getMeasurementSubject.next(this.filterComponent.selectedFilter || null);
    }

    public ngOnDestroy(): void {
        // Save scroll position when leaving the page so it can be restored on return
        this.tablePreferencesService.setPreferences(MeasurementListComponent.tableId, {
            scrollPosition: this.scrollService.getScrollTop()
        });
    }

    private loadSavedFilters() {
        this.apiClientService.getSavedFilters$().subscribe({
            next: (filters) => {
                this.savedFilters = filters;
                this.changeDetectorRef.markForCheck();
            }
        });
    }

    public applySavedFilter(savedFilter: SavedFilterResponse) {
        const filter = deserializeDateRange(JSON.parse(savedFilter.filter) as SelectedFilter);
        this.filterComponent.applyFilter(filter);
        this.showFilter = true;
    }

    public saveCurrentFilter() {
        const modalRef = this.modalService.open(SaveFilterModalComponent);

        modalRef.closed.pipe(take(1)).subscribe((name: string) => {
            if (!name) return;
            this.savingFilter = true;
            this.changeDetectorRef.markForCheck();

            const filter = this.filterComponent.selectedFilter;
            this.apiClientService.createSavedFilter$({ name, filter: JSON.stringify(filter) }).subscribe({
                next: (created) => {
                    this.savedFilters = [...this.savedFilters, created];
                    this.savingFilter = false;
                    this.changeDetectorRef.markForCheck();
                    const title = this.translateService.instant('TOAST_TITLES.SUCCESS') as string;
                    const message = this.translateService.instant(
                        'FILTER.SAVED_FILTERS.TOAST_MESSAGES.SAVE_SUCCESS'
                    ) as string;
                    this.toastService.open(title, message, { type: 'success', progressBar: true });
                },
                error: () => {
                    this.savingFilter = false;
                    this.changeDetectorRef.markForCheck();
                    const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                    const message = this.translateService.instant(
                        'FILTER.SAVED_FILTERS.TOAST_MESSAGES.SAVE_ERROR'
                    ) as string;
                    this.toastService.open(title, message, { type: 'error', progressBar: true });
                }
            });
        });
    }

    public deleteSavedFilter(savedFilter: SavedFilterResponse, event: Event) {
        event.stopPropagation();
        this.apiClientService.deleteSavedFilter$(savedFilter.id).subscribe({
            next: () => {
                this.savedFilters = this.savedFilters.filter((f) => f.id !== savedFilter.id);
                this.changeDetectorRef.markForCheck();
            },
            error: () => {
                const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                const message = this.translateService.instant(
                    'FILTER.SAVED_FILTERS.TOAST_MESSAGES.DELETE_ERROR'
                ) as string;
                this.toastService.open(title, message, { type: 'error', progressBar: true });
            }
        });
    }

    private getCurrentRequest() {
        return this.getMeasurementSubject.value;
    }

    public async navigateToMeasurement(id: string) {
        await this.router.navigate([APP_ROUTES.Measurement, 'details', id]);
    }

    public openMarkerAnnotation(measurement: GetMeasurementResponse) {
        const modalRef = this.modalService.open(MarkerAnnotationModalComponent, {
            size: 'lg'
        });
        const modalData: MarkerAnnotationModalData = {
            measurementId: measurement.id,
            annotation: measurement.annotation,
            markers: [...measurement.markers].sort((a, b) => a.number - b.number)
        };
        (modalRef.componentInstance as MarkerAnnotationModalComponent).data = modalData;

        modalRef.closed.pipe(take(1)).subscribe((hasChanges: boolean) => {
            if (hasChanges) {
                //this.changeDetectorRef.markForCheck();
                this.getMeasurementSubject.next(this.getCurrentRequest());
            }
        });
    }

    public getMarkerAnnotationText(measurement: GetMeasurementResponse) {
        return (
            measurement.markers?.length + ' (' + measurement.markers?.filter((marker) => marker.annotation).length + ')'
        );
    }

    public onSortChange(sortKey: string, sortAscending = true) {
        this.tablePreferencesService.setPreferences(MeasurementListComponent.tableId, {
            sort: { key: sortKey, ascending: sortAscending },
            currentPage: 1
        });
        this.sortKey = sortKey;
        this.sortAscending = sortAscending;
        this.currentPage = 1;
        this.getMeasurementSubject.next(this.getCurrentRequest());
    }

    public async goToImportMeasurement() {
        await this.router.navigate([APP_ROUTES.Measurement, MeasurementRoutes.MeasurementImport]);
    }

    public hasIncludeFlags() {
        return this.getSelectedMeasurementIds().length > 0;
    }

    public allSelectedEditable(): boolean {
        const selectedIds = this.getSelectedMeasurementIds();
        if (selectedIds.length === 0) {
            return false;
        }
        return selectedIds.every((id) => {
            const measurement = this.latestMeasurements.find((m) => m.id === id);
            return measurement?.isEditable === true;
        });
    }

    public hasIncludeFlagsAndPermission() {
        return this.hasIncludeFlags() && this.permissionService.hasAnyPermission([Permission.InternalUser]);
    }

    private getSelectedMeasurementIds(): string[] {
        return Object.keys(this.includeFlags).filter((id) => this.includeFlags[id]);
    }

    public onIncludeToggle(measurementId: string, checked: boolean) {
        this.includeFlags[measurementId] = checked;
    }

    public onCheckedAllChange(checked: boolean) {
        this.resetIncludeFlags(this.latestMeasurements, checked);
        this.changeDetectorRef.markForCheck();
    }

    public resetIncludeFlags(measurements: GetMeasurementResponse[], value: boolean = false) {
        this.includeFlags = measurements.reduce<Record<string, boolean>>((acc, measurement) => {
            acc[measurement.id] = value;
            return acc;
        }, {});
    }

    public clearFilter() {
        this.filterComponent.clearFilter();
        this.filterForm.controls.searchPhrase.setValue('');
        this.currentPage = 1;
        this.tablePreferencesService.setPreferences(MeasurementListComponent.tableId, {
            currentPage: 1,
            searchPhrase: ''
        });
        this.getMeasurementSubject.next(this.getCurrentRequest());
    }

    public onFilterChange(filter: SelectedFilter) {
        this.measurementListFilterService.setFilter(filter);
        this.currentPage = 1;
        this.tablePreferencesService.setPreferences(MeasurementListComponent.tableId, {
            currentPage: 1
        });
        this.getMeasurementSubject.next(filter);
    }

    public onPaginationChange(change: PaginationChange): void {
        this.currentPage = change.page;
        this.pageSize = change.pageSize;
        // Save pagination preferences
        this.tablePreferencesService.setPreferences(MeasurementListComponent.tableId, {
            currentPage: this.currentPage,
            pageSize: this.pageSize
        });
        this.getMeasurementSubject.next(this.getCurrentRequest());
    }

    public exportSelected(event: Event, type: ExportType) {
        event.stopPropagation();
        const selectedIds = this.getSelectedMeasurementIds();

        const downloadType = type === ExportType.Excel ? 'excel' : type === ExportType.CSV ? 'csv' : 'rawData';
        this.downloading[downloadType] = true;
        this.changeDetectorRef.detectChanges();

        const request = { measurementIds: selectedIds, type: type };

        if (selectedIds.length > 10) {
            this.apiClientService
                .createBulkExportJob$(request)
                .pipe(switchMap((job) => this.pollBulkExportJob$(job.jobId)))
                .subscribe({
                    next: (status) => {
                        this.bulkExportProgress = {
                            processedFiles: status.processedFiles,
                            totalFiles: status.totalFiles
                        };
                        this.changeDetectorRef.markForCheck();
                        if (status.status === 'Completed') {
                            if (status.downloadUrl) {
                                window.location.href = status.downloadUrl;
                            } else {
                                const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                                const message = this.translateService.instant(
                                    'FEATURES.MEASUREMENTS.TOAST_MESSAGES.DOWNLOAD_MEASUREMENT_ERROR'
                                ) as string;
                                this.toastService.open(title, message, { type: 'error', progressBar: true });
                            }
                        } else if (status.status === 'Failed') {
                            const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                            const message =
                                status.errorMessage ||
                                (this.translateService.instant(
                                    'FEATURES.MEASUREMENTS.TOAST_MESSAGES.DOWNLOAD_MEASUREMENT_ERROR'
                                ) as string);
                            this.toastService.open(title, message, { type: 'error', progressBar: true });
                        }
                    },
                    error: (error) => {
                        console.error('Error creating bulk export job:', error);
                        const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                        const message = this.translateService.instant(
                            'FEATURES.MEASUREMENTS.TOAST_MESSAGES.DOWNLOAD_MEASUREMENT_ERROR'
                        ) as string;
                        this.toastService.open(title, message, { type: 'error', progressBar: true });
                    },
                    complete: () => {
                        this.bulkExportProgress = null;
                        this.downloading[downloadType] = false;
                        this.changeDetectorRef.markForCheck();
                    }
                });
            return;
        }

        this.apiClientService.downloadMeasurementsExportFile$(request).subscribe({
            next: (response) => {
                const filename = `measurements-export.zip`;
                const blob = response.body;
                if (blob) {
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename;
                    a.click();
                    window.URL.revokeObjectURL(url);
                }
            },
            error: (error) => {
                console.error('Error downloading export file:', error);
                const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                const message = this.translateService.instant(
                    'FEATURES.MEASUREMENTS.TOAST_MESSAGES.DOWNLOAD_MEASUREMENT_ERROR'
                ) as string;
                this.toastService.open(title, message, { type: 'error', progressBar: true });
            },
            complete: () => {
                this.downloading[downloadType] = false;
                this.changeDetectorRef.markForCheck();
            }
        });
    }

    private pollBulkExportJob$(jobId: string) {
        return timer(0, 2000).pipe(
            take(60),
            concatMap(() => this.apiClientService.getBulkExportJobStatus$(jobId)),
            takeWhile(
                (status: BulkExportJobResponse) => status.status !== 'Completed' && status.status !== 'Failed',
                true
            )
        );
    }

    public exportRawData(event: Event) {
        this.exportSelected(event, ExportType.RawData);
    }

    public moveSelectedMeasurements(event: Event): void {
        event.stopPropagation();
        if (this.isDeleteLoading) return;
        const selectedIds = this.getSelectedMeasurementIds();

        const modalRef = this.modalService.open(MoveLocationModalComponent);
        const modalData: MoveLocationModalData = { measurementIds: selectedIds };
        (modalRef.componentInstance as MoveLocationModalComponent).data = modalData;

        this.isMoveLoading = true;
        this.changeDetectorRef.markForCheck();

        modalRef.closed.pipe(take(1)).subscribe((success: boolean) => {
            this.isMoveLoading = false;
            this.changeDetectorRef.markForCheck();

            if (success) {
                const title = this.translateService.instant('TOAST_TITLES.SUCCESS') as string;
                const message = this.translateService.instant(
                    'FEATURES.MEASUREMENTS.TOAST_MESSAGES.MOVE_MEASUREMENTS_SUCCESS'
                ) as string;
                this.toastService.open(title, message, { type: 'success', progressBar: true });
                this.getMeasurementSubject.next(this.getCurrentRequest());
            } else {
                const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                const message = this.translateService.instant(
                    'FEATURES.MEASUREMENTS.TOAST_MESSAGES.MOVE_MEASUREMENTS_ERROR'
                ) as string;
                this.toastService.open(title, message, { type: 'error', progressBar: true });
            }
        });

        modalRef.dismissed.pipe(take(1)).subscribe(() => {
            this.isMoveLoading = false;
            this.changeDetectorRef.markForCheck();
        });
    }

    public confirmDeleteSelectedMeasurements(event: Event): void {
        event.stopPropagation();
        if (
            this.isDeleteLoading ||
            this.isMoveLoading ||
            this.loadingMeasurementIds.length > 0 ||
            !this.allSelectedEditable()
        ) {
            return;
        }

        const selectedIds = this.getSelectedMeasurementIds();
        let deletedCount = 0;
        const modalRef = this.modalService.open(ConfirmModalComponent);
        const helper = ModalHelper.getConfirmModalInstanceHelper(modalRef);
        helper.componentInstance.model = {
            titleLanguageKey: 'FEATURES.MEASUREMENTS.TITLES.DELETE_MEASUREMENTS',
            bodyLanguageKey: 'FEATURES.MEASUREMENTS.TEXTS.CONFIRM_DELETE_MEASUREMENTS',
            bodyParams: { count: selectedIds.length },
            cancelLanguageKey: 'BUTTONS.CANCEL',
            confirmLanguageKey: 'BUTTONS.CONFIRM'
        };
        this.isDeleteLoading = true;
        this.changeDetectorRef.markForCheck();

        helper.confirmation$
            .pipe(
                switchMap((confirmed) => {
                    if (!confirmed) return EMPTY;
                    this.loadingMeasurementIds = [...this.loadingMeasurementIds, ...selectedIds];
                    return this.apiClientService.deleteMeasurements$(selectedIds).pipe(
                        tap(() => {
                            deletedCount = selectedIds.length;
                            for (const id of selectedIds) {
                                this.includeFlags[id] = false;
                            }
                            this.measurementsLength -= deletedCount;
                            this.deletedMeasurementIdsSubject.next([
                                ...this.deletedMeasurementIdsSubject.value,
                                ...selectedIds
                            ]);
                            this.changeDetectorRef.markForCheck();
                        })
                    );
                }),
                untilDestroyed(this),
                finalize(() => {
                    this.isDeleteLoading = false;
                    this.loadingMeasurementIds = this.loadingMeasurementIds.filter((id) => !selectedIds.includes(id));
                    if (deletedCount > 0) {
                        this.currentPage = Math.min(
                            this.currentPage,
                            Math.max(1, Math.ceil(this.measurementsLength / this.pageSize))
                        );
                        this.getMeasurementSubject.next(this.getCurrentRequest());
                    }
                    this.changeDetectorRef.markForCheck();
                })
            )
            .subscribe({
                next: () => {
                    const title = this.translateService.instant('TOAST_TITLES.SUCCESS') as string;
                    const message = this.translateService.instant(
                        'FEATURES.MEASUREMENTS.TOAST_MESSAGES.DELETE_MEASUREMENTS_SUCCESS',
                        { count: deletedCount }
                    ) as string;
                    this.toastService.open(title, message, { type: 'success', progressBar: true });
                },
                error: () => {
                    const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                    const message = this.translateService.instant(
                        'FEATURES.MEASUREMENTS.TOAST_MESSAGES.DELETE_MEASUREMENTS_ERROR'
                    ) as string;
                    this.toastService.open(title, message, { type: 'error', progressBar: true });
                }
            });
    }

    public confirmDeleteMeasurement(event: Event, measurement: GetMeasurementResponse) {
        event.stopPropagation();
        if (this.isDeleteLoading || this.loadingMeasurementIds.includes(measurement.id)) {
            return;
        }
        const modalRef = this.modalService.open(ConfirmModalComponent);
        const modalInstanceHelper = ModalHelper.getConfirmModalInstanceHelper(modalRef);

        modalInstanceHelper.componentInstance.model = {
            titleLanguageKey: 'FEATURES.MEASUREMENTS.TITLES.DELETE_MEASUREMENT',
            bodyLanguageKey: 'FEATURES.MEASUREMENTS.TEXTS.CONFIRM_DELETE_MEASUREMENT',
            cancelLanguageKey: 'BUTTONS.CANCEL',
            confirmLanguageKey: 'BUTTONS.CONFIRM'
        };

        modalInstanceHelper.confirmation$
            .pipe(
                untilDestroyed(this),
                switchMap((confirm) => {
                    if (confirm) {
                        this.loadingMeasurementIds.push(measurement.id);
                        this.changeDetectorRef.markForCheck();
                        return this.apiClientService.deleteMeasurement$(measurement.id);
                    }
                    return EMPTY;
                }),
                finalize(() => {
                    this.loadingMeasurementIds = this.loadingMeasurementIds.filter((id) => id != measurement.id);
                    this.changeDetectorRef.markForCheck();
                })
            )
            .subscribe({
                next: () => {
                    const title = this.translateService.instant('TOAST_TITLES.SUCCESS') as string;
                    const message = this.translateService.instant(
                        'FEATURES.MEASUREMENTS.TOAST_MESSAGES.DELETE_MEASUREMENT_SUCCESS'
                    ) as string;

                    this.toastService.open(title, message, { type: 'success', progressBar: true });
                    this.deletedMeasurementIdsSubject.next([
                        ...this.deletedMeasurementIdsSubject.value,
                        measurement.id
                    ]);
                    this.measurementsLength--;
                },
                error: () => {
                    const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                    const message = this.translateService.instant(
                        'FEATURES.MEASUREMENTS.TOAST_MESSAGES.DELETE_MEASUREMENT_ERROR'
                    ) as string;

                    this.toastService.open(title, message, { type: 'error', progressBar: true });
                }
            });
    }
}
